import { computed, type ComputedRef } from 'vue';
import { useMarketStore } from '@/stores/market';
import { type MarketDto, ProvisionType, QuantityUnit } from '@containex/portal-backend-dto';
import { marketsApi, zipCodeApi } from '@containex/portal-backend-api-client';
import { useMemoize } from '@vueuse/core';
import { httpClient } from '@/common/api/http-client';
import { useProvisionTypeQuery } from './provision-type';

export interface MarketQuery {
    market: ComputedRef<MarketDto | null>;
    marketCurrency: ComputedRef<string | undefined>;
    availableMarkets: ComputedRef<MarketDto[]>;
    currentSalesChannelId: ComputedRef<string | undefined>;
    isMonthlyRentalMarket: ComputedRef<boolean>;
}

export interface MarketAction {
    initMarketData(): Promise<string>;

    setMarket(marketCode: string): void;

    zipCodeExistsInCurrentMarket(zipCode: string): Promise<boolean>;

    zipCodeExistsInCountryCode(zipCode: string, countryCode: string): Promise<boolean>;
}

export function useMarketQuery(): MarketQuery {
    const store = useMarketStore();
    const { currentProvisionType } = useProvisionTypeQuery();

    return {
        market: computed(() => store.market),
        marketCurrency: computed(() => store.market?.currency),
        availableMarkets: computed(() => store.availableMarkets),
        currentSalesChannelId: computed(() => {
            if (currentProvisionType.value === ProvisionType.Rental) {
                return store.market?.rentalChannelId ?? undefined;
            } else if (currentProvisionType.value === ProvisionType.Sales) {
                return store.market?.salesChannelId ?? undefined;
            }

            return undefined;
        }),
        isMonthlyRentalMarket: computed(
            () =>
                currentProvisionType.value === ProvisionType.Rental &&
                store.market?.rentalUnit === QuantityUnit.PER_MONTH
        ),
    };
}

export function useMarketAction(): MarketAction {
    const store = useMarketStore();
    const { currentProvisionType } = useProvisionTypeQuery();

    const zipCodeExistsApiCall = useMemoize(zipCodeApi.zipCodeExists.bind(zipCodeApi));

    return {
        setMarket(marketCode: string): void {
            store.setMarket(marketCode);
        },
        async zipCodeExistsInCurrentMarket(zipCode: string): Promise<boolean> {
            if (store.market == null || currentProvisionType.value == null) {
                return false;
            }

            const { data } = await zipCodeExistsApiCall(httpClient, {
                countryCode: store.market.code,
                zipCode,
                provisionType: currentProvisionType.value,
            });

            return data.exists;
        },
        async zipCodeExistsInCountryCode(zipCode: string, countryCode: string): Promise<boolean> {
            if (currentProvisionType.value == null) {
                return false;
            }

            const { data } = await zipCodeExistsApiCall(httpClient, {
                countryCode,
                zipCode,
                provisionType: currentProvisionType.value,
            });

            return data.exists;
        },
        async initMarketData() {
            const { data } = await marketsApi.fetchMarkets(httpClient);

            store.availableMarkets = data.availableMarkets;
            store.setIdentifiedZipCode(data.zipCode ?? store.identifiedZipCode);
            return data.identifiedMarket.code;
        },
    };
}
